import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import {
  FileText, Plus, Search, MoreVertical, Edit, Trash2, Copy, RefreshCw,
  Eye, Code, Wand2, Mail, Check, X, LayoutGrid
} from 'lucide-react';
import EmailBuilder from '../components/EmailBuilder';

const API = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  { value: 'general', label: 'General' },
  { value: 'welcome', label: 'Welcome' },
  { value: 'newsletter', label: 'Newsletter' },
  { value: 'promotional', label: 'Promotional' },
  { value: 'transactional', label: 'Transactional' },
  { value: 'followup', label: 'Follow-up' },
];

export default function MarketingTemplates() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [templates, setTemplates] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [saving, setSaving] = useState(false);
  const [editorTab, setEditorTab] = useState('visual');
  const [builderOpen, setBuilderOpen] = useState(false);
  const [builderTemplate, setBuilderTemplate] = useState(null);
  
  const [form, setForm] = useState({
    name: '',
    subject: '',
    html_content: '',
    plain_content: '',
    category: 'general'
  });
  
  const token = sessionStorage.getItem('auth_token');
  const limit = 12;

  useEffect(() => {
    if (token) {
      fetchTemplates();
    }
  }, [token, page, search, category]);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        token,
        page: page.toString(),
        limit: limit.toString()
      });
      if (search) params.append('search', search);
      if (category) params.append('category', category);
      
      const response = await axios.get(`${API}/api/marketing/templates?${params}`);
      if (response.data.success) {
        setTemplates(response.data.templates);
        setTotal(response.data.total);
      }
    } catch (error) {
      console.error('Failed to fetch templates', error);
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  const openDialog = (template = null) => {
    if (template) {
      setEditingTemplate(template);
      setForm({
        name: template.name,
        subject: template.subject || '',
        html_content: template.html_content || '',
        plain_content: template.plain_content || '',
        category: template.category || 'general'
      });
    } else {
      setEditingTemplate(null);
      setForm({
        name: '',
        subject: '',
        html_content: '',
        plain_content: '',
        category: 'general'
      });
    }
    setEditorTab('visual');
    setDialogOpen(true);
  };

  const saveTemplate = async () => {
    if (!form.name.trim()) {
      toast.error('Template name is required');
      return;
    }
    
    try {
      setSaving(true);
      if (editingTemplate) {
        await axios.put(
          `${API}/api/marketing/templates/${editingTemplate.id}?token=${token}`,
          form
        );
        toast.success('Template updated');
      } else {
        await axios.post(`${API}/api/marketing/templates?token=${token}`, form);
        toast.success('Template created');
      }
      setDialogOpen(false);
      fetchTemplates();
    } catch (error) {
      toast.error('Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  const deleteTemplate = async (template) => {
    if (!window.confirm(`Delete template "${template.name}"?`)) return;
    
    try {
      await axios.delete(`${API}/api/marketing/templates/${template.id}?token=${token}`);
      toast.success('Template deleted');
      fetchTemplates();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to delete template';
      toast.error(errorMsg);
    }
  };

  const duplicateTemplate = async (template) => {
    try {
      await axios.post(
        `${API}/api/marketing/templates/${template.id}/duplicate?token=${token}`
      );
      toast.success('Template duplicated');
      fetchTemplates();
    } catch (error) {
      toast.error('Failed to duplicate template');
    }
  };

  const createDefaults = async () => {
    try {
      setLoading(true);
      const response = await axios.post(
        `${API}/api/marketing/templates/create-defaults?token=${token}`
      );
      if (response.data.success) {
        toast.success(`Created ${response.data.created} default templates`);
        fetchTemplates();
      }
    } catch (error) {
      toast.error('Failed to create default templates');
    } finally {
      setLoading(false);
    }
  };

  const openBuilder = (template = null) => {
    setBuilderTemplate(template);
    setBuilderOpen(true);
  };

  const handleBuilderSave = async (data) => {
    try {
      setSaving(true);
      const payload = {
        name: data.name,
        subject: data.subject,
        html_content: data.html_content,
        category: 'general',
        builder_blocks: JSON.stringify(data.blocks || [])
      };
      
      if (builderTemplate) {
        await axios.put(
          `${API}/api/marketing/templates/${builderTemplate.id}?token=${token}`,
          payload
        );
        toast.success('Template updated');
      } else {
        await axios.post(`${API}/api/marketing/templates?token=${token}`, payload);
        toast.success('Template created');
      }
      
      setBuilderOpen(false);
      setBuilderTemplate(null);
      fetchTemplates();
    } catch (error) {
      toast.error('Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  const openPreview = (template) => {
    setPreviewTemplate(template);
    setPreviewOpen(true);
  };

  const insertVariable = (variable) => {
    setForm(prev => ({
      ...prev,
      html_content: prev.html_content + `{{${variable}}}`
    }));
  };

  const totalPages = Math.ceil(total / limit);

  // Show builder in full screen mode
  if (builderOpen) {
    return (
      <EmailBuilder
        initialBlocks={builderTemplate?.builder_blocks ? JSON.parse(builderTemplate.builder_blocks) : []}
        templateName={builderTemplate?.name || ''}
        templateSubject={builderTemplate?.subject || ''}
        onSave={handleBuilderSave}
        onCancel={() => {
          setBuilderOpen(false);
          setBuilderTemplate(null);
        }}
      />
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6" data-testid="marketing-templates">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Email Templates</h1>
          <p className="text-gray-500 mt-1">
            Create and manage reusable email templates for your campaigns
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={createDefaults}>
            <Wand2 className="h-4 w-4 mr-2" />
            Create Defaults
          </Button>
          <Button variant="outline" onClick={() => openBuilder()}>
            <LayoutGrid className="h-4 w-4 mr-2" />
            Visual Builder
          </Button>
          <Button onClick={() => openDialog()}>
            <Plus className="h-4 w-4 mr-2" />
            HTML Editor
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Search templates..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            className="pl-10"
          />
        </div>
        <Select value={category || "all"} onValueChange={(value) => { setCategory(value === "all" ? "" : value); setPage(1); }}>
          <SelectTrigger className="w-[160px]">
            <SelectValue placeholder="All Categories" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {CATEGORIES.map(cat => (
              <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button variant="outline" onClick={fetchTemplates}>
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
        </Button>
      </div>

      {/* Templates Grid */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="h-8 w-8 animate-spin text-gray-400" />
        </div>
      ) : templates.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center">
            <FileText className="h-12 w-12 mx-auto text-gray-300 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No templates yet</h3>
            <p className="text-gray-500 mb-6">Create your first email template or start with defaults</p>
            <div className="flex justify-center gap-3">
              <Button variant="outline" onClick={createDefaults}>
                <Wand2 className="h-4 w-4 mr-2" />
                Create Defaults
              </Button>
              <Button onClick={() => openDialog()}>
                <Plus className="h-4 w-4 mr-2" />
                Create Template
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {templates.map((template) => (
            <Card key={template.id} className="hover:shadow-md transition-shadow">
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-amber-100 rounded-lg">
                      <Mail className="h-5 w-5 text-amber-600" />
                    </div>
                    <div>
                      <CardTitle className="text-base line-clamp-1">{template.name}</CardTitle>
                      <div className="flex items-center gap-2 mt-1">
                        <Badge variant="outline" className="text-xs">
                          {template.category}
                        </Badge>
                        {template.is_global && (
                          <Badge variant="secondary" className="text-xs">Global</Badge>
                        )}
                      </div>
                    </div>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm">
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => openPreview(template)}>
                        <Eye className="h-4 w-4 mr-2" />
                        Preview
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => openBuilder(template)}>
                        <LayoutGrid className="h-4 w-4 mr-2" />
                        Visual Editor
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => openDialog(template)}>
                        <Code className="h-4 w-4 mr-2" />
                        HTML Editor
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => duplicateTemplate(template)}>
                        <Copy className="h-4 w-4 mr-2" />
                        Duplicate
                      </DropdownMenuItem>
                      {!template.is_global && (
                        <DropdownMenuItem 
                          onClick={() => deleteTemplate(template)}
                          className="text-red-600"
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Delete
                        </DropdownMenuItem>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </CardHeader>
              <CardContent>
                {template.subject && (
                  <p className="text-sm text-gray-600 line-clamp-2 mb-3">
                    Subject: {template.subject}
                  </p>
                )}
                <div className="flex items-center justify-between text-sm text-gray-500">
                  <span>Used {template.usage_count || 0} times</span>
                  <Button variant="ghost" size="sm" onClick={() => openPreview(template)}>
                    <Eye className="h-4 w-4 mr-1" />
                    Preview
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-gray-500">
            Showing {((page - 1) * limit) + 1} to {Math.min(page * limit, total)} of {total} templates
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
            >
              Next
            </Button>
          </div>
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>{editingTemplate ? 'Edit Template' : 'Create Template'}</DialogTitle>
            <DialogDescription>
              Design your email template with HTML and personalization variables
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Template Name *</Label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g., Welcome Email"
                />
              </div>
              <div className="space-y-2">
                <Label>Category</Label>
                <Select
                  value={form.category}
                  onValueChange={(value) => setForm(prev => ({ ...prev, category: value }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map(cat => (
                      <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Default Subject Line</Label>
              <Input
                value={form.subject}
                onChange={(e) => setForm(prev => ({ ...prev, subject: e.target.value }))}
                placeholder="e.g., Welcome to {{company_name}}!"
              />
            </div>

            {/* Personalization Variables */}
            <div className="flex flex-wrap gap-2 p-3 bg-blue-50 rounded-lg">
              <span className="text-sm text-blue-700 font-medium mr-2">Insert Variable:</span>
              {['first_name', 'last_name', 'email', 'company', 'unsubscribe_url'].map(v => (
                <Button
                  key={v}
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-7 text-xs"
                  onClick={() => insertVariable(v)}
                >
                  {`{{${v}}}`}
                </Button>
              ))}
            </div>

            {/* Editor Tabs */}
            <Tabs value={editorTab} onValueChange={setEditorTab}>
              <TabsList>
                <TabsTrigger value="visual">
                  <Eye className="h-4 w-4 mr-2" />
                  Preview
                </TabsTrigger>
                <TabsTrigger value="html">
                  <Code className="h-4 w-4 mr-2" />
                  HTML Code
                </TabsTrigger>
                <TabsTrigger value="plain">
                  <FileText className="h-4 w-4 mr-2" />
                  Plain Text
                </TabsTrigger>
              </TabsList>
              
              <TabsContent value="visual" className="mt-4">
                <div className="border rounded-lg bg-white">
                  {form.html_content ? (
                    <iframe
                      srcDoc={form.html_content}
                      className="w-full h-[400px] border-0"
                      title="Email Preview"
                    />
                  ) : (
                    <div className="h-[400px] flex items-center justify-center text-gray-400">
                      <div className="text-center">
                        <Mail className="h-12 w-12 mx-auto mb-2" />
                        <p>Add HTML content to see preview</p>
                      </div>
                    </div>
                  )}
                </div>
              </TabsContent>
              
              <TabsContent value="html" className="mt-4">
                <Textarea
                  value={form.html_content}
                  onChange={(e) => setForm(prev => ({ ...prev, html_content: e.target.value }))}
                  placeholder="<!DOCTYPE html><html>..."
                  className="font-mono text-sm h-[400px]"
                />
              </TabsContent>
              
              <TabsContent value="plain" className="mt-4">
                <Textarea
                  value={form.plain_content}
                  onChange={(e) => setForm(prev => ({ ...prev, plain_content: e.target.value }))}
                  placeholder="Plain text version of your email (optional)"
                  className="h-[400px]"
                />
              </TabsContent>
            </Tabs>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={saveTemplate} disabled={saving}>
              {saving ? 'Saving...' : (editingTemplate ? 'Update Template' : 'Create Template')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle>{previewTemplate?.name}</DialogTitle>
            <DialogDescription>
              Subject: {previewTemplate?.subject || 'No subject'}
            </DialogDescription>
          </DialogHeader>
          <div className="border rounded-lg bg-white overflow-hidden">
            {previewTemplate?.html_content ? (
              <iframe
                srcDoc={previewTemplate.html_content}
                className="w-full h-[500px] border-0"
                title="Email Preview"
              />
            ) : (
              <div className="h-[300px] flex items-center justify-center text-gray-400">
                <p>No content to preview</p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPreviewOpen(false)}>
              Close
            </Button>
            <Button onClick={() => { setPreviewOpen(false); openDialog(previewTemplate); }}>
              <Edit className="h-4 w-4 mr-2" />
              Edit Template
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
